import { ref, type Ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useRouter } from 'vue-router'
import { api } from '@/plugins/axios'
import { extractMessage } from '@/helpers'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/useToast'
import { AUTH_API_CONFIG } from '@/constants/api'
import type {
  DataLogin,
  User,
  LoginCredentials,
  AuthResult,
  LoginResponse,
  WhoAmIResponse,
  FormChangePassword,
  Message,
} from '@/types/auth.d'

export const useAuth = defineStore('auth', () => {
  // Dependencies
  const router = useRouter()
  const { t } = useI18n()

  // State
  const lastTokenSetAt = ref(0)
  const isLoggedIn: Ref<boolean> = ref(false)
  const dataLogin: Ref<DataLogin | null> = ref(null)
  const user: Ref<User | null> = ref(null)
  const loading: Ref<boolean> = ref(false)
  const error: Ref<string | null> = ref(null)
  const message: Ref<Message | null> = ref(null)
  const tempCredentials: Ref<LoginCredentials | null> = ref(null)
  const showOtpModal: Ref<boolean> = ref(false)

  /**
   * Check current login status
   */
  /**
   * Check current login status
   * @param setLoading - If false, won't set the global loading state (default: true)
   */
  async function checkLogin(setLoading: boolean = true): Promise<void> {
    if (setLoading) {
      loading.value = true
    }
    error.value = null
    message.value = null

    try {
      const token = localStorage.getItem('auth_token')

      if (!token) {
        isLoggedIn.value = false
        user.value = null
        if (setLoading) {
          loading.value = false
        }
        return
      }

      const response = await api.get<WhoAmIResponse>(AUTH_API_CONFIG.WHO_AM_I, {
        withCredentials: true,
      })

      if (response.status === 200) {
        if (response.data?.data) {
          isLoggedIn.value = true
          user.value = response.data.data

          if (token) {
            dataLogin.value = {
              token,
              role: response.data.data.role,
            }
          }
        } else if (token) {
          isLoggedIn.value = true
          user.value = null
        } else {
          isLoggedIn.value = false
          user.value = null
        }
      } else {
        isLoggedIn.value = false
        user.value = null
      }
    } catch (err: unknown) {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        isLoggedIn.value = false
        user.value = null
      }
      error.value = err instanceof Error ? err.message : 'Failed to check login status'
    } finally {
      if (setLoading) {
        loading.value = false
      }
    }
  }

  /**
   * Login with username and password
   * Returns success=false with require2fa=true if 2FA is needed
   */
  async function loginAdmin(credentials: LoginCredentials): Promise<AuthResult> {
    loading.value = true
    error.value = null
    message.value = null
    tempCredentials.value = null

    try {
      const response = await api.post<LoginResponse>(AUTH_API_CONFIG.LOGIN, credentials)

      return await handleSuccessfulLogin(response.data)
    } catch (err: unknown) {
      if (api.isAxiosError(err)) {
        const status = err.response?.status
        const errorData = err.response?.data as LoginResponse | undefined

        if (status === 403 && errorData && Number(errorData.code) === 10) {
          tempCredentials.value = credentials
          message.value = errorData.message
          setTimeout(() => {
            showOtpModal.value = true
          }, 500)

          return {
            success: false,
            require2fa: true,
            message: errorData.message,
          }
        }
      }

      return handleError(err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Submit OTP code for 2FA authentication
   * Sends username, password, and code together to /login/admin
   * This is called when 403 with code 10 is received during initial login
   */
  async function submitOtp(otpCode: string): Promise<AuthResult> {
    loading.value = true
    error.value = null

    if (!tempCredentials.value) {
      error.value = 'Session expired. Please login again.'
      message.value = {
        header: t('validation.openError'),
        body: error.value,
      }
      loading.value = false
      return { success: false, message: message.value }
    }

    try {
      const payload: LoginCredentials = {
        username: tempCredentials.value.username,
        password: tempCredentials.value.password,
        two_fa_code: otpCode,
      }

      const response = await api.post<LoginResponse>(AUTH_API_CONFIG.LOGIN, payload)

      // Check if token was received
      if (response.data?.data?.token) {
        tempCredentials.value = null // Clear sensitive data
        showOtpModal.value = false // Close modal
        return await handleSuccessfulLogin(response.data)
      } else {
        // OTP was incorrect or other error
        message.value = response.data.message
        return { success: false, message: message.value }
      }
    } catch (err: unknown) {
      if (api.isAxiosError(err)) {
        const errorData = err.response?.data as LoginResponse | undefined

        if (errorData && Number(errorData.code) === 10) {
          message.value = errorData.message
          showOtpModal.value = true
          return {
            success: false,
            require2fa: true,
            message: errorData.message,
          }
        }
      }

      return handleError(err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Handle successful login response
   * Sets token and updates login state
   */
  async function handleSuccessfulLogin(responseData: LoginResponse): Promise<AuthResult> {
    if (responseData?.data?.token) {
      // Store token
      localStorage.setItem('auth_token', responseData.data.token)
      lastTokenSetAt.value = Date.now()
      dataLogin.value = responseData.data
      message.value = responseData.message
      isLoggedIn.value = true

      return { success: true, message: message.value }
    }

    return { success: false, message: responseData.message }
  }

  /**
   * Change password
   */
  async function changePassword(
    FormChangePassword: FormChangePassword,
    setLoading: boolean = true,
  ): Promise<AuthResult> {
    if (setLoading) {
      loading.value = true
    }
    error.value = null
    message.value = null

    console.log('🔑 changePassword called with:', {
      hasOldPassword: !!FormChangePassword.old_password,
      hasNewPassword: !!FormChangePassword.new_password,
      hasTwoFaCode: !!FormChangePassword.two_fa_code,
      twoFaCodeLength: FormChangePassword.two_fa_code?.length,
    })

    try {
      const response = await api.put(AUTH_API_CONFIG.CHANGE_PASSWORD, FormChangePassword)
      console.log('📥 changePassword response:', {
        code: response.data.code,
        hasMessage: !!response.data.message,
      })

      // Check if code 10 is returned (2FA required) even in successful response
      if (response.data.code === 10) {
        message.value = response.data.message
        showOtpModal.value = true
        return { success: false, require2fa: true, message: message.value || undefined }
      }
      if (response.data.code === 0) {
        message.value = response.data.message
        showOtpModal.value = false
        return { success: true, message: message.value || undefined }
      } else {
        message.value = response.data.message
        showOtpModal.value = false
        return { success: false, message: message.value || undefined }
      }
    } catch (err: unknown) {
      console.log('❌ changePassword error:', err)
      if (api.isAxiosError(err)) {
        const errorData = err.response?.data as LoginResponse | undefined
        console.log('🔍 Error data:', {
          code: errorData?.code,
          hasMessage: !!errorData?.message,
          status: err.response?.status,
        })

        // Handle 2FA requirement (code 10)
        if (errorData && Number(errorData.code) === 10) {
          message.value = errorData.message
          showOtpModal.value = true
          return { success: false, require2fa: true, message: errorData.message }
        }

        // Handle other errors without logging out the user
        const errorMessage = extractMessage(err)
        message.value = errorMessage || {
          header: t('validation.openError'),
          body: t('validation.passowrdError'),
        }
        error.value = message.value.body
        showOtpModal.value = false
        return { success: false, message: message.value }
      }

      // For non-axios errors, extract message without logout
      const errorMessage = extractMessage(err)
      message.value = errorMessage || {
        header: t('validation.openError'),
        body: t('validation.passowrdError'),
      }
      error.value = message.value.body
      return { success: false, message: message.value }
    } finally {
      loading.value = false
    }
  }

  /**
   * Handle authentication errors
   */
  function handleError(err: unknown): AuthResult {
    isLoggedIn.value = false
    user.value = null

    const errorMessage = extractMessage(err)
    if (errorMessage) {
      message.value = errorMessage
    } else {
      message.value = {
        header: t('validation.openError'),
        body: t('validation.passowrdError'),
      }
    }

    error.value = message.value?.body || 'Authentication error'

    return { success: false, message: message.value }
  }
  /**
   * Logout user and clear session
   */
  async function logout(): Promise<void> {
    const { showToast } = useToast()

    localStorage.removeItem('auth_token')
    lastTokenSetAt.value = Date.now()
    isLoggedIn.value = false
    user.value = null
    dataLogin.value = null
    tempCredentials.value = null
    message.value = null
    error.value = null
    showOtpModal.value = false

    showToast({
      header: t('validation.logoutSuccess'),
      body: t('validation.logoutMessage'),
      color: 'green',
    })

    await router.push('/')
  }

  /* getting user info */
  const isTwoFAEnabled = computed(() => {
    if (user.value && user.value.two_fa_enabled !== undefined) {
      return Boolean(user.value.two_fa_enabled)
    }
    return false
  })

  const hasTwoFAProperty = computed(() => {
    return user.value && user.value.two_fa_enabled !== undefined
  })

  return {
    // State
    isLoggedIn,
    user,
    loading,
    error,
    message,
    lastTokenSetAt,
    dataLogin,
    tempCredentials,
    showOtpModal,

    // Computed
    isTwoFAEnabled,
    hasTwoFAProperty,
    // Actions
    checkLogin,
    loginAdmin,
    submitOtp,
    logout,
    changePassword,
  }
})
